import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { Chip, Show, useIsMobile } from '@side/pantry';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import {
  CoverPhoto,
  ListingDetailLine,
  THUMB_HEIGHT,
  THUMB_WIDTH,
} from '@/components/ListingSummaryCard/helpers';
import { useListingSummary } from '@/components/ListingSummaryCard/useListingSummary';
import { TrackOfferEvent } from '$analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from '$analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { useAppFlags } from '$launch-darkly/app-flags';
import type { CreateOfferFormData } from '../types';

const containerSx = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'flex-start', sm: 'center' },
  gap: { xs: 1, sm: 2 },
  px: 2,
  py: 1.5,
  border: '1px solid',
  borderColor: 'other.outlineBorder',
  borderRadius: '2px',
  backgroundColor: 'common.white',
} as const;

const Loading = ({ hideCoverPhoto }: { hideCoverPhoto: boolean }) => (
  <Box sx={containerSx}>
    <Show when={!hideCoverPhoto}>
      <Skeleton
        variant="rectangular"
        width={THUMB_WIDTH}
        height={THUMB_HEIGHT}
        sx={{ borderRadius: 1, flexShrink: 0 }}
      />
    </Show>
    <Stack sx={{ flex: 1, gap: 0.5, width: '100%' }}>
      <Skeleton width="60%" height={20} />
      <Skeleton width="80%" height={16} />
    </Stack>
  </Box>
);

export const MyListingSummary = () => {
  const listingId = useWatch<CreateOfferFormData, 'listingId'>({
    name: 'listingId',
  });
  const { txmLinkOffersListings } = useAppFlags();
  const isMobile = useIsMobile();

  const { listing, loading } = useListingSummary({
    listingId,
    skipCoverPhoto: isMobile,
    skipMlsInformation: true,
  });

  const trackOfferEvent = useTrackOfferAnalytics();
  // Gated rather than useEffectOnce: this component renders null on an unlinked
  // offer, so the card-mount event must fire only once the card is shown.
  const shouldShowCard = !!txmLinkOffersListings && !!listingId;
  useEffect(() => {
    if (shouldShowCard) {
      trackOfferEvent(TrackOfferEvent.MyListingSummaryMountStep2, {
        listingId,
      });
    }
  }, [shouldShowCard, listingId, trackOfferEvent]);

  if (!txmLinkOffersListings || !listingId) return null;

  if (loading || !listing) return <Loading hideCoverPhoto={isMobile} />;

  const {
    coverPhotoUrl,
    formattedFullAddress,
    interactionStatus,
    formattedSellers,
  } = listing;

  return (
    <Box sx={containerSx}>
      <Show when={!isMobile}>
        <CoverPhoto
          coverPhotoUrl={coverPhotoUrl}
          id={listingId}
          sizes={`${THUMB_WIDTH}px`}
          sx={{
            flexShrink: 0,
            width: THUMB_WIDTH,
            height: THUMB_HEIGHT,
            borderRadius: 1,
          }}
        />
      </Show>

      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
          {formattedFullAddress}
        </Typography>
        <ListingDetailLine
          interactionStatus={interactionStatus}
          formattedSellers={formattedSellers}
        />
      </Stack>

      <Chip
        label="My listing"
        color="secondary"
        variant="outlined"
        size="xs"
        sx={{ flexShrink: 0, order: { xs: -1, sm: 0 } }}
      />
    </Box>
  );
};
